import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import integrationsService from '../../services/integrationsService';
import { Check, X, ExternalLink } from 'lucide-react';

const PROVIDERS = [
  {
    id: 'chrome',
    name: 'Chrome',
    description: 'Sync bookmarks, history, and browsing data',
    icon: '/assets/images/logos/chrome.png',
    color: 'from-blue-500 to-blue-600'
  },
  {
    id: 'drive',
    name: 'Drive',
    description: 'Access and search your Google Drive files',
    icon: '/assets/images/logos/drive.png',
    color: 'from-green-500 to-green-600'
  },
  {
    id: 'dropbox',
    name: 'Dropbox',
    description: 'Connect your Dropbox files and folders',
    icon: '/assets/images/logos/dropbox.png',
    color: 'from-blue-600 to-blue-700'
  },
  {
    id: 'notion',
    name: 'Notion',
    description: 'Sync your Notion pages and databases',
    icon: '/assets/images/logos/notion.png',
    color: 'from-gray-800 to-gray-900'
  },
  {
    id: 'kindle',
    name: 'Kindle',
    description: 'Access your Kindle library and highlights',
    icon: '/assets/images/logos/kindle.png',
    color: 'from-orange-500 to-orange-600'
  },
  {
    id: 'chatgpt',
    name: 'ChatGPT',
    description: 'Connect ChatGPT for AI-powered features',
    icon: '/assets/images/logos/chatgpt.png',
    color: 'from-teal-500 to-teal-600'
  }
];

const ConnectedAccountsSection = () => {
  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [integrations, setIntegrations] = useState([]);
  const [connecting, setConnecting] = useState(null);
  const [message, setMessage] = useState({ type: '', text: '' });

  useEffect(() => {
    loadIntegrations();
  }, [user]);

  const loadIntegrations = async () => {
    if (!user) return;
    
    try {
      setLoading(true);
      const data = await integrationsService.getUserIntegrations(user.id);
      setIntegrations(data);
    } catch (error) {
      console.error('Error loading integrations:', error);
      setMessage({ type: 'error', text: 'Failed to load integrations' });
    } finally {
      setLoading(false);
    }
  };

  const isConnected = (providerId) => {
    return integrations.some(int => int.provider === providerId && int.is_active);
  };

  const getIntegrationData = (providerId) => {
    return integrations.find(int => int.provider === providerId && int.is_active);
  };

  const handleConnect = async (providerId) => {
    try {
      setConnecting(providerId);
      setMessage({ type: '', text: '' });
      
      await integrationsService.connectProvider(providerId);
      
      // Note: The actual connection happens via OAuth redirect
      // So we don't update state here - it will be updated after redirect
    } catch (error) {
      console.error('Error connecting provider:', error);
      setMessage({ type: 'error', text: `Failed to connect ${providerId}` });
      setConnecting(null);
    }
  };

  const handleDisconnect = async (providerId) => {
    if (!confirm(`Are you sure you want to disconnect ${providerId}?`)) return;
    
    try {
      setMessage({ type: '', text: '' });
      await integrationsService.disconnectProvider(user.id, providerId);
      await loadIntegrations();
      setMessage({ type: 'success', text: `${providerId} disconnected successfully` });
      setTimeout(() => setMessage({ type: '', text: '' }), 3000);
    } catch (error) {
      console.error('Error disconnecting provider:', error);
      setMessage({ type: 'error', text: `Failed to disconnect ${providerId}` });
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h2 className="text-2xl font-bold text-gray-900">Connected Accounts</h2>
        <p className="text-gray-600 mt-1">
          Connect third-party services to enhance your Swirl experience
        </p>
      </div>

      {/* Message */}
      {message.text && (
        <div className={`flex items-center space-x-2 p-4 rounded-lg ${
          message.type === 'success' ? 'bg-green-50 text-green-800' : 'bg-red-50 text-red-800'
        }`}>
          {message.type === 'success' ? <Check size={20} /> : <X size={20} />}
          <span>{message.text}</span>
        </div>
      )}

      {/* Providers Grid */}
      <div className="grid gap-4">
        {PROVIDERS.map(provider => {
          const connected = isConnected(provider.id);
          const integrationData = getIntegrationData(provider.id);
          const isConnecting = connecting === provider.id;

          return (
            <div
              key={provider.id}
              className="border border-gray-200 rounded-lg p-6 hover:shadow-md transition-shadow"
            >
              <div className="flex items-start justify-between">
                <div className="flex items-start space-x-4">
                  <div className="w-12 h-12 rounded-lg bg-white border border-gray-200 flex items-center justify-center p-2">
                    <img 
                      src={provider.icon} 
                      alt={`${provider.name} logo`}
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center space-x-2">
                      <h3 className="text-lg font-semibold text-gray-900">
                        {provider.name}
                      </h3>
                      {connected && (
                        <span className="flex items-center space-x-1 text-xs bg-green-100 text-green-800 px-2 py-1 rounded-full">
                          <Check size={12} />
                          <span>Connected</span>
                        </span>
                      )}
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {provider.description}
                    </p>
                    {connected && integrationData?.provider_email && (
                      <p className="text-xs text-gray-500 mt-2">
                        Connected as: {integrationData.provider_email}
                      </p>
                    )}
                    {connected && integrationData?.last_sync_at && (
                      <p className="text-xs text-gray-500">
                        Last synced: {new Date(integrationData.last_sync_at).toLocaleString()}
                      </p>
                    )}
                  </div>
                </div>

                <div className="flex flex-col space-y-2">
                  {connected ? (
                    <>
                      <button
                        onClick={() => handleDisconnect(provider.id)}
                        className="px-4 py-2 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                      >
                        Disconnect
                      </button>
                      {(provider.id === 'notion' || provider.id === 'slack') && (
                        <button
                          className="px-4 py-2 text-sm text-gray-600 hover:bg-gray-100 rounded-lg transition-colors flex items-center space-x-1"
                        >
                          <span>Settings</span>
                          <ExternalLink size={14} />
                        </button>
                      )}
                    </>
                  ) : (
                    <button
                      onClick={() => handleConnect(provider.id)}
                      disabled={isConnecting}
                      className={`px-4 py-2 text-sm bg-gradient-to-r ${provider.color} text-white rounded-lg hover:opacity-90 transition-opacity disabled:opacity-50`}
                    >
                      {isConnecting ? 'Connecting...' : 'Connect'}
                    </button>
                  )}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Info Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <h4 className="font-semibold text-blue-900 mb-2">
          Why connect accounts?
        </h4>
        <ul className="text-sm text-blue-800 space-y-1">
          <li>• Automatically save content from connected services</li>
          <li>• Search across all your connected platforms</li>
          <li>• Get AI insights from your integrated data</li>
          <li>• Sync focuses and collections with your tools</li>
        </ul>
      </div>
    </div>
  );
};

export default ConnectedAccountsSection;

